export const MARKET = {
  US: 'US',
  CA: 'CA',
} as const;
export const MARKETS = [MARKET.US, MARKET.CA] as const;

export type Market = (typeof MARKETS)[number];

export const MARKET_SOURCE = {
  UNRESOLVED: 'unresolved',
  SIGNUP: 'signup',
  KYC: 'kyc',
} as const;
export const MARKET_SOURCES = [
  MARKET_SOURCE.UNRESOLVED,
  MARKET_SOURCE.SIGNUP,
  MARKET_SOURCE.KYC,
] as const;

export type MarketSource = (typeof MARKET_SOURCES)[number];

const MARKET_SOURCE_PRIORITY = {
  [MARKET_SOURCE.UNRESOLVED]: 0,
  [MARKET_SOURCE.SIGNUP]: 1,
  [MARKET_SOURCE.KYC]: 2,
} as const satisfies Record<MarketSource, number>;

export function getMarketSourcePriority(source: MarketSource | undefined): number {
  return source === undefined ? -1 : MARKET_SOURCE_PRIORITY[source];
}

export const MARKET_LOCALES = {
  US: 'en-US',
  CA: 'en-CA',
} as const satisfies Record<Market, string>;

export type MarketLocale = (typeof MARKET_LOCALES)[Market];
export type AppLanguage = 'en';

export const MARKET_COUNTRY_CODES = {
  US: 'US',
  CA: 'CA',
} as const satisfies Record<Market, string>;

export type MarketCountryCode = (typeof MARKET_COUNTRY_CODES)[Market];

export const MARKET_FIAT_CURRENCIES = {
  US: 'USD',
  CA: 'CAD',
} as const satisfies Record<Market, string>;

export type MarketFiatCurrency = (typeof MARKET_FIAT_CURRENCIES)[Market];

const CANADA_PATH_PREFIX = '/ca';
const INTERNAL_LOCALE_PATH_PATTERN = /^\/(en-US|en-CA)(?=\/|$)/;
const LOCALE_LANGUAGES = {
  [MARKET_LOCALES.US]: 'en',
  [MARKET_LOCALES.CA]: 'en',
} as const satisfies Record<MarketLocale, AppLanguage>;

export function isMarket(value: unknown): value is Market {
  return typeof value === 'string' && MARKETS.includes(value as Market);
}

export function getLocaleForMarket(market: Market): MarketLocale {
  return MARKET_LOCALES[market];
}

export function getMarketForLocale(locale: MarketLocale): Market {
  return locale === MARKET_LOCALES.CA ? MARKET.CA : MARKET.US;
}

export function getLanguageForLocale(locale: MarketLocale): AppLanguage {
  return LOCALE_LANGUAGES[locale];
}

export function getCountryCodeForMarket(market: Market): MarketCountryCode {
  return MARKET_COUNTRY_CODES[market];
}

export function getFiatCurrencyForMarket(market: Market): MarketFiatCurrency {
  return MARKET_FIAT_CURRENCIES[market];
}

export function getMarketFromPathname(pathname: string): Market {
  return pathname === CANADA_PATH_PREFIX || pathname.startsWith(`${CANADA_PATH_PREFIX}/`)
    ? MARKET.CA
    : MARKET.US;
}

export function getMarketFromRoutePathname(pathname: string): Market {
  const locale = pathname.match(INTERNAL_LOCALE_PATH_PATTERN)?.[1] as MarketLocale | undefined;
  return locale ? getMarketForLocale(locale) : getMarketFromPathname(pathname);
}

export function getPathnameForMarket(pathname: string, market: Market): string {
  const normalizedPathname = pathname || '/';
  const pathWithoutMarket = getMarketFromPathname(normalizedPathname) === MARKET.CA
    ? normalizedPathname.slice(CANADA_PATH_PREFIX.length) || '/'
    : normalizedPathname;

  if (market === MARKET.US) {
    return pathWithoutMarket;
  }

  return pathWithoutMarket === '/'
    ? CANADA_PATH_PREFIX
    : `${CANADA_PATH_PREFIX}${pathWithoutMarket}`;
}

export function getMarketRedirectPathname(pathname: string, market: Market): string | null {
  if (getMarketFromPathname(pathname) === market) {
    return null;
  }

  return getPathnameForMarket(pathname, market);
}

export function getMarketRedirectLocation(
  pathname: string,
  search: string,
  hash: string,
  market: Market,
): string | null {
  const targetPathname = getMarketRedirectPathname(pathname, market);
  return targetPathname ? `${targetPathname}${search}${hash}` : null;
}

export function getPublicPathnameFromInternalLocale(pathname: string): string | null {
  const locale = pathname.match(INTERNAL_LOCALE_PATH_PATTERN)?.[1] as MarketLocale | undefined;

  if (!locale) {
    return null;
  }

  const pathWithoutLocale = pathname.slice(locale.length + 1) || '/';
  return getPathnameForMarket(pathWithoutLocale, getMarketForLocale(locale));
}
